/**
 * DroppableAIRefSection (M2 — kompakte Leiste)
 *
 * Schlanke horizontale Stil-Anker-Leiste: Titel + Hinweis links, 4 kompakte
 * Droppable-Slots rechts. Die Droppable-IDs/Data (`ai-ref-slot-<n>`) bleiben
 * unverändert, damit das Drag&Drop aus der Bilderliste weiter funktioniert.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useDroppable } from '@dnd-kit/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Sparkles, Image as ImageIcon } from 'lucide-react';
import { Badge, Loading } from '@/components/v2/ui';
import { getAIReferenceImages, type AIRefSlot } from '@/lib/api/products';
import { AI_ROLES } from './AIReferencesSubTab';

interface DroppableAIRefSectionProps {
  productId: string;
  savingSlot: number | null;
}

export function DroppableAIRefSection({ productId, savingSlot }: DroppableAIRefSectionProps) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['ai-ref-slots', productId],
    queryFn: () => getAIReferenceImages(productId),
    staleTime: 30_000,
  });

  const slots = data?.slots ?? [];
  const filledCount = slots.filter((s) => !s.is_empty).length;

  return (
    <div className="mb-4 rounded-lg border border-primary-200/70 dark:border-primary-900/40 bg-primary-50/40 dark:bg-primary-900/10 px-3 py-2">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Sparkles className="h-4 w-4 text-primary-500 shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                {t('productImages.aiRef.title')}
              </span>
              <Badge variant="secondary">{filledCount} / 4</Badge>
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
              {t('productImages.aiRef.hint')}
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {[1, 2, 3, 4].map((idx) => (
            <DroppableAIRefSlot
              key={idx}
              slot={slots.find((s) => s.slot === idx) ?? null}
              slotIndex={idx}
              saving={savingSlot === idx}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Single droppable slot (private) — kompakt (64px)
// ============================================================================

function DroppableAIRefSlot({
  slot,
  slotIndex,
  saving,
}: {
  slot: AIRefSlot | null;
  slotIndex: number;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const isEmpty = !slot || slot.is_empty;
  const roleInfo = AI_ROLES.find(
    (r) => r.value.toLowerCase() === slot?.role?.toLowerCase(),
  );

  const { setNodeRef, isOver } = useDroppable({
    id: `ai-ref-slot-${slotIndex}`,
    data: { type: 'ai-ref-slot', slot: slotIndex },
  });

  const ringClass = isOver
    ? isEmpty
      ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/20'
      : 'ring-2 ring-amber-500 bg-amber-50 dark:bg-amber-900/20'
    : '';

  return (
    <div
      ref={setNodeRef}
      title={isEmpty ? t('productImages.aiRef.slot', { n: slotIndex }) : roleInfo?.label}
      className={`relative h-16 w-16 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 flex items-center justify-center overflow-hidden transition-all ${
        isEmpty ? 'border-dashed' : ''
      } ${ringClass}`}
    >
      <span className="absolute top-0.5 right-1 text-2xs text-neutral-400 dark:text-neutral-500">{slotIndex}</span>
      {saving ? (
        <Loading size="sm" />
      ) : isEmpty ? (
        <ImageIcon className="h-4 w-4 text-neutral-400 dark:text-neutral-500" />
      ) : (
        <FilledSlotContent slot={slot} roleInfo={roleInfo} isOver={isOver} />
      )}
    </div>
  );
}

function FilledSlotContent({
  slot,
  roleInfo,
  isOver,
}: {
  slot: AIRefSlot;
  roleInfo: typeof AI_ROLES[number] | undefined;
  isOver: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      {slot.thumbnail_url ? (
        <img src={slot.thumbnail_url} alt={roleInfo?.label ?? `Slot ${slot.slot}`} className="w-full h-full object-cover" />
      ) : (
        <ImageIcon className="h-4 w-4 text-neutral-400" />
      )}
      {roleInfo && (
        <span className={`absolute bottom-0 inset-x-0 text-center text-2xs font-medium text-white ${roleInfo.color}`}>
          {roleInfo.icon}
        </span>
      )}
      {isOver && (
        <span className="absolute inset-0 flex items-center justify-center bg-amber-500/70 text-2xs font-medium text-white">
          {t('productImages.aiRef.replace')}
        </span>
      )}
    </>
  );
}
